<template>
  <v-container
    fluid
    fill-height
    class="no-gutter"
  >
    <Saving v-if="saving" />
    <Loading v-if="loading" />
    <v-container
      v-if="correlationData && !loading"
      fluid
      fill-height
      :class="{ paused: paused }"
    >
      <v-dialog
        v-model="updating"
        hide-overlay
        persistent
        width="300"
      >
        <v-card
          color="light"
          dark
        >
          <v-card-text>
            {{ strings.updating }}...
            <v-progress-linear
              indeterminate
              color="white"
              class="mb-0"
            />
          </v-card-text>
        </v-card>
      </v-dialog>
      <v-layout
        align-space-around
        justify-center
        column
      >
        <v-flex>
          <v-layout
            align-center
            justify-space-between
            row
            class="header"
          >
            <v-flex>
              <h2>{{ correlationData.name }}</h2>
            </v-flex>
          </v-layout>
        </v-flex>
        <v-flex>
          <v-layout
            align-center
            justify-space-between
            row
          >
            <v-flex class="btn--line">
              <v-btn
                round
                color="primary"
                @click="onSaveButton"
              >
                <v-icon color="text">
                  mdi-content-save
                </v-icon>
              </v-btn>
            </v-flex>
            <v-flex>
              <v-btn
                round
                color="primary"
                @click="onPauseButton"
              >
                <v-icon
                  v-if="paused"
                  color="text"
                >
                  mdi-play
                </v-icon>
                <v-icon
                  v-else
                  color="text"
                >
                  mdi-pause
                </v-icon>
              </v-btn>
            </v-flex>
            <v-flex>
              <v-btn
                round
                color="primary"
                @click="onMapButton(correlationData.id)"
              >
                <v-icon color="text">
                  mdi-google-maps
                </v-icon>
              </v-btn>
            </v-flex>
          </v-layout>
        </v-flex>
        <v-flex>
          <v-layout
            align-space-between
            justify-space-between
            row
          >
            <v-flex>
              <div class="data-header text-xs-left">
                {{strings.confidence }}:
              </div>
            </v-flex>
            <v-flex>
              <div class="data-content text-xs-right">
                {{ poiDisplayConfidence }}
              </div>
            </v-flex>
          </v-layout>
          <v-layout
            align-space-between
            justify-space-between
            row
          >
            <v-flex>
              <div class="data-header text-xs-left">
               {{strings.radiusConfidence }}:
              </div>
            </v-flex>
            <v-flex>
              <div class="data-content text-xs-right">
                {{ poiDisplayRadiusConfidence }}
              </div>
            </v-flex>
          </v-layout>
          <v-layout
            align-space-between
            justify-space-between
            row
          >
            <v-flex>
              <div class="data-header text-xs-left">
                {{ strings.group }}:
              </div>
            </v-flex>
            <v-flex>
              <div class="data-content text-xs-right">
                {{ activeGroup }}
              </div>
            </v-flex>
          </v-layout>
          <v-layout
            align-space-between
            justify-space-between
            row
          >
            <v-flex>
              <div class="data-header text-xs-left">
                {{ strings.maxPipeLength }}:
              </div>
            </v-flex>
            <v-flex>
              <div class="data-content text-xs-right">
                {{ poiMaxPipeLength }}
              </div>
            </v-flex>
          </v-layout>
          <v-layout
            align-space-between
            justify-space-between
            row
          >
            <v-flex>
              <div class="data-header text-xs-left">
                {{ strings.minPipeLength }}:
              </div>
            </v-flex>
            <v-flex>
              <div class="data-content text-xs-right">
                {{ poiMinPipeLength }}
              </div>
            </v-flex>
          </v-layout>
          <v-layout
            align-space-between
            justify-space-between
            row
          >
            <v-flex>
              <div class="data-header text-xs-left">
                {{ strings.avPipeLength }}:
              </div>
            </v-flex>
            <v-flex>
              <div class="data-content text-xs-right">
                {{ poiAvgPipeLength }}
              </div>
            </v-flex>
          </v-layout>
          <v-layout
            align-space-between
            justify-space-between
            row
          >
            <v-flex>
              <div class="data-header text-xs-left">
                {{ strings.filter }}:
              </div>
            </v-flex>
            <v-flex>
              <div class="data-content text-xs-right">
                {{ pois[selectedPdpIndex].filter }}
              </div>
            </v-flex>
          </v-layout>
          <v-layout
            align-space-between
            justify-space-between
            row
          >
            <v-flex>
              <div class="data-header text-xs-left">
                {{ strings.correlations }}:
              </div>
            </v-flex>
            <v-flex>
              <div class="data-content text-xs-right">
                {{ poiCount }}
              </div>
            </v-flex>
          </v-layout>
        </v-flex>
        <v-flex
          v-touch:swipe.left="onChartSwipeLeft"
          v-touch:swipe.right="onChartSwipeRight"
        >
          <v-layout
            align-space-between
            justify-space-between
            column
          >
            <v-flex>
              <highcharts
                ref="chartDiv"
                :options="chartOptions"
              />
            </v-flex>
            <v-flex>
              <v-layout
                align-space-between
                justify-space-between
                row
              >
                <v-flex>
                  <div class="data-header text-xs-left">
                    {{ strings.correlation }}:
                  </div>
                </v-flex>
                <v-flex>
                  <div class="data-content text-xs-right">
                    {{ selectedPdpIndex + 1 }}
                  </div>
                </v-flex>
              </v-layout>
            </v-flex>
            <v-flex>
              <v-layout
                align-space-between
                justify-space-between
                row
              >
                <v-flex>
                  <div class="data-header text-xs-left">
                   {{ strings.confidence }}:
                  </div>
                </v-flex>
                <v-flex>
                  <div class="data-content text-xs-right">
                    {{ pois[selectedPdpIndex].confidence.toFixed(2) }}%
                  </div>
                </v-flex>
              </v-layout>
            </v-flex>
            <v-flex>
              <v-layout
                align-space-between
                justify-space-between
                row
              >
                <v-flex>
                  <div class="data-header text-xs-left">
                    {{ strings.correlationDate }}:
                  </div>
                </v-flex>
                <v-flex>
                  <div class="data-content text-xs-right">
                    {{ poi.timeStamp | format_date }}
                  </div>
                </v-flex>
              </v-layout>
            </v-flex>
            <v-flex>
              <v-layout
                v-if="correlationData"
                align-space-between
                justify-space-between
                row
              >
                <v-flex>
                  <div class="data-header text-xs-left">
                    {{ strings.loggers }}:
                  </div>
                </v-flex>
                <v-flex>
                  <div class="data-content text-xs-right">
                    {{ pois[selectedPdpIndex].leftLogger.loggerName }} -
                    {{ pois[selectedPdpIndex].rightLogger.loggerName }}
                  </div>
                </v-flex>
              </v-layout>
            </v-flex>
          </v-layout>
        </v-flex>
        <v-flex
          v-if="correlationData"
          ref="pipeDiagramDiv"
        >
          <pipe-diagram
            :id="pois[selectedPdpIndex].idGuid"
            :detail="createPipeDetail"
            :points="points"
            :index="highestPointIndex"
            :peak-m-s="currentPeakMs"
          />
        </v-flex>
        <v-flex v-if="isOnSite">
          <v-layout>
            <v-flex class="btn--line">
              <v-btn
                round
                :color="isLeak ? 'primary' : 'light'"
                :input-value="result === 'leak'"
                @click="onLeakButton"
              >
                <v-icon color="text">
                  mdi-pipe-leak
                </v-icon>
              </v-btn>
            </v-flex>
            <v-flex>
              <v-btn
                round
                :color="isNotLeak ? 'primary' : 'light'"
                :input-value="result === 'noleak'"
                @click="onNoLeakButton"
              >
                <LeakCancel class="text--text" />
              </v-btn>
            </v-flex>
            <v-flex>
              <v-btn
                round
                color="danger"
                :input-value="result === 'escalate'"
                @click="onEscalateButton"
              >
                <v-icon color="text">
                  mdi-transfer-up
                </v-icon>
              </v-btn>
            </v-flex>
          </v-layout>
          <v-container
            fluid
            class="no-gutter"
          >
            <v-layout
              v-if="result === 'leak' || isLeak"
              align-space-around
              justify-space-around
              column
              class="section"
            >
              <v-flex>
                <v-layout
                  align-space-around
                  justify-space-around
                  column
                >
                  <v-flex>
                    <h4 class="text-xs-left">
                      {{ strings.leaks }}:
                    </h4>
                  </v-flex>
                  <v-flex v-if="isLeak">
                    <v-btn
                      block
                      round
                      color="primary"
                      @click="addFoundLeak"
                    >
                      <AddLeak class="text--text" />
                    </v-btn>
                  </v-flex>
                </v-layout>
              </v-flex>
              <v-flex
                v-for="(leak, index) in leaks"
                :key="index"
              >
                <FoundLeak
                  :leak="leak"
                  :poi-coords="poiCoords"
                  :highlight-missing-g-p-s="highlightMissingGPS"
                  :highlight-missing-classification="
                    highlightMissingClassification
                  "
                  :highlight-missing-asset-leak-type="highlightMissingAssetLeakType"
                  :highlight-missing-equipment-used="highlightMissingEquipmentUsed"
                  :highlight-missing-leak-type="highlightMissingLeakType"
                  :highlight-missing-work-order="highlightMissingWorkOrder"
                  :hasPlastic="hasPlastic"
                  @delete="onLeakDelete(index)"
                />
              </v-flex>
            </v-layout>
            <v-layout
              v-if="result === 'noleak' || isNotLeak"
              align-space-around
              justify-space-around
              column
              class="section"
            >
              <v-flex>
                <v-layout
                  align-space-around
                  justify-space-around
                  column
                >
                  <v-flex>
                    <no-leak-found
                      :leak="noLeak"
                      :highlight-missing-equipment-used="highlightMissingEquipmentUsed"
                      :highlight-missing-reason="highlightMissingLeakType"
                      @delete="onNoLeakDelete()"
                    />
                  </v-flex>
                </v-layout>
              </v-flex>
            </v-layout>
          </v-container>
        </v-flex>
        <v-flex>
          <image-list
            :id="correlationData.id"
            :images="images"
            :imageState="imageCheck"
            @updating="setUpdating"
          />
        </v-flex>
        <v-flex>
          <CommentList
            :comments="comments"
            @setComments="setCommentVisible"
          />
          <v-layout
            v-if="showComments"
            align-space-around
            justify-space-around
            row
          >
            <v-flex class="comment">
              <v-textarea
                v-model="newComment"
                box
                name="comment-entry"
                label="Enter a new comment"
                style="caret-color: #fff;"
              />
              <v-btn
                dark
                small
                left
                fab
                color="primary"
                class="add-comment"
                @click="addComment()"
              >
                <v-icon color="text">
                  mdi-comment-plus
                </v-icon>
              </v-btn>
            </v-flex>
          </v-layout>
          <v-layout>
            <v-flex v-if="canBeCompleted">
              <v-btn
                round
                block
                :color="!completed ? 'primary' : '#67a55f'"
                @click="onCompleteButton"
              >
                <v-icon color="text">
                  mdi-check
                </v-icon>
              </v-btn>
            </v-flex>
          </v-layout>
        </v-flex>
        <v-flex>
          <v-btn
            v-if="!isOnSite"
            color="primary"
            round
            block
            @click="onSiteButtonClicked"
          >
            {{ strings.onSite }}
          </v-btn>
          <v-btn
            v-else-if="isOnSite"
            color="primary"
            round
            block
            @click="onLeaveSiteButton"
          >
            {{ strings.leaveSite }}
          </v-btn>
          <v-btn
            v-else
            color="primary"
            round
            block
            @click="onAnotherSiteButton"
          >
           {{ strings.switchToThisSite }}
          </v-btn>
          <v-btn
            color="primary"
            round
            block
            class="mt-3"
            @click="onReturnToGroups"
          >
            {{ strings.returnToGroups }}
          </v-btn>
        </v-flex>
      </v-layout>
      <AreYouSureComment
        v-model="areYouSureEscalate"
        action="escalating this poi"
        @answer="onEscalateAnswer($event)"
      />
      <AreYouSure
        v-model="areYouSureComplete"
        action="completing this poi"
        @answer="onCompleteAnswer($event)"
      />
      <AreYouSureComment
        v-model="areYouSureCompleteComment"
        action="completing this poi (Reason Other). Please enter a comment to continue"
        @answer="onCompleteCommentAnswer($event)"
      />
    </v-container>
    <v-dialog
      v-model="missingGPSDialog"
      max-width="300"
    >
      <v-card>
        <v-card-title
          class="headline"
        >
          {{ strings.leaksMissingGps }}
        </v-card-title>
        <v-card-text>
          {{ strings.leaksMissingGpsMessage }}
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn
            color="primary"
            flat="flat"
            @click="missingGPSDialog = false"
          >
            {{ strings.ok }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
    <v-dialog
      v-model="missingClassificationDialog"
      max-width="300"
    >
      <v-card>
        <v-card-title
          class="headline"
        >
          {{ strings.leaksMissingClassification }}
        </v-card-title>
        <v-card-text>
          {{ strings.leaksMissingClassMessage}}
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn
            color="primary"
            flat="flat"
            @click="missingClassificationDialog = false"
          >
            {{ strings.ok }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
    <v-dialog
      v-model="missingRequiredLeakDetails"
      max-width="300"
    >
      <v-card>
        <v-card-title
          class="headline"
        >
          {{ strings.leaksMissingRequired }}
        </v-card-title>
        <v-card-text>
          {{ strings.leaksMissingRequiredMessage }}
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn
            color="primary"
            flat="flat"
            @click="missingRequiredLeakDetails = false"
          >
            {{ strings.ok }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script>
import { PageEventHandlers } from "../mixins/pageEventHandlers";
import LeakCancel from "../Icons/LeakCancel.vue";
import AddLeak from "../Icons/AddLeak";
import { GetRandomSalt } from "../helpers/Utils.js";
import Chart from "../models/Chart.js";
import { FoundLeak as FoundLeakModel, NoLeakFound as NoLeakModel, SiteTime } from "@primayer/poi-classes";
import CommentList from "../components/CommentList";
import FoundLeak from "../components/FoundLeak";
import AreYouSure from "../components/AreYouSure.vue";
import AreYouSureComment from "../components/AreYouSureComment.vue";
import Saving from "../components/Overlays/Saving.vue";
import Loading from "../components/Overlays/Loading.vue";
import { mapGetters } from "vuex";
import PipeDiagram from "../components/PipeDiagram.vue";
import CommonComponents from "@primayer/common-components";
import { CreateLogEntryObj } from "../helpers/Utils";
import ImageList from "../components/ImageList.vue";
import NoLeakFound from "../components/NoLeakFound.vue";

export default {
  name: "DataDetail",
  components: {
    PipeDiagram,
    LeakCancel,
    AddLeak,
    CommentList,
    FoundLeak,
    AreYouSure,
    AreYouSureComment,
    Saving,
    Loading,
    ...CommonComponents,
    ImageList,
    NoLeakFound
  },
  mixins: [PageEventHandlers],
  data() {
    return {
      selectedPdpIndex: 0,
      maxConfidence: null,
      chartWidth: 350,
      saving: false,
      areYouSureEscalate: false,
      areYouSureComplete: false,
      areYouSureCompleteComment: false,
      missingGPSDialog: false,
      highlightMissingGPS: false,
      missingClassificationDialog: false,
      highlightMissingClassification: false,
      highlightMissingAssetLeakType: false,
      highlightMissingEquipmentUsed: false,
      highlightMissingLeakType: false,
      missingRequiredLeakDetails: false,
      highlightMissingWorkOrder: false,
      pdpIndex: [0],
      newComment: null,
      CorrelationObj: null,
      leaks: [],
      noLeak: [],
      updating: false,
      result: "",
      isLeak: false,
      isNotLeak: true,
      escalate: false,
      completed: false,
      showComments: false
    };
  },
  computed: {
    ...mapGetters("login", ["technicianId"]),
    ...mapGetters("group", ["activeGroup"]),
    ...mapGetters("poi", ["activeGroupPoi"]),
    ...mapGetters("page", ["strings"]),
    ...mapGetters("settings", ["setting"]),
    technicianData() {
      if (this.correlationData && this.correlationData.technicianData != null) {
        return this.correlationData.technicianData;
      }
      return null;
    },
    chartPeak() {
      var max = Math.max.apply(null, this.poi.chartData);
      return this.poi.chartData.findIndex(l => l == max);
    },
    currentPeakMs() {
      if (this.poi && this.chartPeak) {
        return this.poi.distance;
      } else {
        return 0;
      }
    },
    correlationData() {
      const x = this.activeGroupPoi;
      return x;
    },
    loading() {
      return false;
    },
    chartOptions() {
      if (this.pois) {
        return this.MakeChart().chartOptions;
      } else {
        return null;
      }
    },
    pois() {
      return this.correlationData.pois;
    },
    poi() {
      return this.correlationData.pois[this.selectedPdpIndex];
    },
    poiCoords() {
      if (this.poi && this.pois) {
        return { lat: this.pois[0].latitude, lng: this.pois[0].longitude };
      } else {
        return null;
      }
    },
    poiDisplayConfidence() {
      if (this.pois && this.poi.confidence) {
        const maxConfidence = Math.max.apply(
          Math,
          this.pois.map(p => {
            return p.confidence;
          })
        );
        return Math.round(maxConfidence) + "%";
      }
      return 0;
    },
    poiDisplayRadiusConfidence() {
      if (this.pois && this.poi.confidence) {
        const sum = this.pois.reduce((a, b) => a + b.confidence, 0);
        const average = sum / this.pois.length || 0;
        return Math.round(average) + "%";
      }
      return 0;
    },
    pipeLengths() {
      var arr = [];
      if (this.pois) {
        this.pois.forEach(poi => {
          let totalLength = poi.pipes.reduce(function(accumulator, item) {
            return accumulator + item.length;
          }, 0);
          arr.push(totalLength);
        });
      }
      return arr;
    },
    poiMaxPipeLength() {
      if (this.pipeLengths.length > 0) {
        const minPipeLength = Math.max.apply(Math, this.pipeLengths);
        return Math.round(minPipeLength);
      }
      return "N/A";
    },
    poiMinPipeLength() {
      if (this.pipeLengths.length > 0) {
        const minPipeLength = Math.min.apply(Math, this.pipeLengths);
        return Math.round(minPipeLength);
      }
      return "N/A";
    },
    poiAvgPipeLength() {
      if (this.pipeLengths.length > 0) {
        const sum = this.pipeLengths.reduce((a, b) => a + b, 0);
        const average = sum / this.pipeLengths.length || 0;
        return Math.round(average);
      }
      return "N/A";
    },
    poiFilter() {
      if (this.poi && this.poi.mainFilter) {
        return this.poi.mainFilter;
      }
      return "N/A";
    },
    poiCount() {
      return this.pois.length;
    },
    poiCorrelationDetail() {
      if (this.poi && this.pois)
        return this.pois[this.selectedPdpIndex].correlationDetail;
      else return null;
    },
    pixelsPerMS() {
      if (!this.poiCorrelationDetail) {
        return null;
      }
      return this.getPixelsPerMillisecond(
        this.chartWidth,
        Number(this.poiCorrelationDetail.minTimeDelay),
        Number(this.poiCorrelationDetail.maxTimeDelay)
      );
    },
    pipeDiagramOffset() {
      if (!this.poiCorrelationDetail) {
        return null;
      }
      return this.getPixelOffset(
        Number(this.poiCorrelationDetail.minTimeDelay),
        Number(this.poiCorrelationDetail.peak),
        Number(this.pixelsPerMS)
      );
    },
    createPipeDetail() {
      if (!this.poiCorrelationDetail) {
        return null;
      }

      if (this.chartWidth <= 0) this.updateChartWidth();

      let left = this.findPercentage(
        this.getPercentage(
          Number(this.pipeDiagramOffset),
          Number(this.chartWidth)
        ),
        Number(this.poiCorrelationDetail.pipeLength)
      );
      let right = Number(this.poiCorrelationDetail.pipeLength) - left;
      return {
        distanceFromLeft: left.toFixed(2),
        distanceFromRight: right.toFixed(2),
        pipeLength: Number(this.poiCorrelationDetail.pipeLength),
        leftName: `${this.poi.leftLogger.loggerName} (${this.poi.leftLogger.asset})`,
        rightName: `(${this.poi.rightLogger.asset}) ${this.poi.rightLogger.loggerName}`,
        pipes: this.poi.pipes,
        distance: this.poi.distance
      };
    },
    points() {
      return this.pois[this.selectedPdpIndex].chartData;
    },
    highestPointIndex() {
      return this.findMaxIndex(this.pois[this.selectedPdpIndex].chartData);
    },
    pdpDisplayDate() {
      let date = new Date(this.pois[this.selectedPdpIndex].timeStamp);
      return date.toLocaleDateString();
    },
    comments() {
      if (this.correlationData != null && this.correlationData.comments) {
        return this.correlationData.comments;
      }
      return [];
    },
    images() {
      if (this.technicianData != null && this.technicianData.photoURLs) {
        return this.technicianData.photoURLs;
      }
      return [];
    },
    canBeCompleted() {
      var check1 = this.imageCheck;
      var check2 = (this.leaks.length >= 1 && this.isLeak) || this.isNotLeak;
      return check1 && check2;
    },
    imageCheck() {
      var hasImage = this.images.length > 0;
      if(this.setting.mandatoryImage && !hasImage) {
        return false;
      }

      if(this.hasCorrelationEquip) {
        if(this.setting.correlationImage && !hasImage) {
          return false;
        }
      }
      return true;

    },
    hasCorrelationEquip() {
      var res = false;
      this.leaks.forEach(leak => {
        if(leak.equipmentUsed.includes(2) || leak.equipmentUsed.includes(3) || leak.equipmentUsed.includes(4)) {
          res = true;
        }
      })
      
      if(!res) {
        if(this.noLeak && this.noLeak.equipmentUsed) {
          if(this.noLeak.equipmentUsed.includes(2) || this.noLeak.equipmentUsed.includes(3) || this.noLeak.equipmentUsed.includes(4)) {
            res = true;
          }
        }
      }

      return res;
    },
    hasPlastic() {
      var res = false;
      this.pois.forEach(poi => {
        poi.pipes.forEach(pipe => {
          if(pipe.material == 6 || pipe.material == 8 || pipe.material == 9) {
            res = true;
          }
        });
      })

      return res;
    }
  },
  updated() {
    this.updateChartWidth();
  },
  mounted() {
    if (this.technicianData && this.technicianData.leaks) {
      this.leaks = JSON.parse(JSON.stringify(this.technicianData.leaks));
      // this.leaks = Object.assign({},this.technicianData.leaks);
      this.noLeak = JSON.parse(JSON.stringify(this.technicianData.noLeak));
      this.result = this.technicianData.result;
      this.isLeak = this.technicianData.isLeak;
      this.isNotLeak = this.technicianData.isNotLeak;
      this.escalate = this.technicianData.escalate;
      this.completed = this.technicianData.completed;
    }
  },
  methods: {
    onReturnToGroups() {
      this.$router
        .push({ name: "outstanding", params: { id: this.activeGroup } })
        .catch(() => {});
    },
    async addComment() {
      if (this.newComment.trim() !== "" && !this.updating) {
        this.updating = true;
        let entry = {
          userId: this.userId,
          username: this.authUser.username,
          comment: this.newComment,
          date: new Date(),
          poiId: this.correlationData.id
        };

        await this.$store.dispatch("poi/AddCommentToPoi", entry);

        this.newComment = null;
        this.updating = false;
      }
    },
    MakeChart() {
      let chartData = this.pois[this.selectedPdpIndex].chartData;
      let confidence = this.pois[this.selectedPdpIndex].confidence;
      let id = this.pois[this.selectedPdpIndex].id;
      if (chartData !== null && chartData !== undefined) {
        let data = {
          data: chartData,
          confidence: confidence,
          interval: 4.6875,
          startPoint: -1500
        };
        if (id === null || id === undefined) {
          data.container =
            "analytics-" + GetRandomSalt(24) + "-" + GetRandomSalt(24);
        } else {
          data.container = "analytics-" + id + "-" + GetRandomSalt(24);
        }
        return new Chart(data);
      } else {
        return new Chart();
      }
    },
    CalculateMaxConfidence() {
      let pois = this.poi.pois;
      if (pois !== null && pois !== undefined) {
        let highest = -1;
        if (pois.length === 1) {
          highest = pois[0].confidence;
        } else if (pois.length > 1) {
          for (let i = 0; i < this.pois.length; i++) {
            if (pois[i].confidence > highest) {
              highest = pois[i].confidence;
            }
          }
        } else {
          highest = -1;
        }
        return highest;
      } else {
        return -1;
      }
    },
    onChartSwipeLeft() {
      if (this.selectedPdpIndex >= this.pois.length - 1) {
        this.selectedPdpIndex = 0;
      } else {
        this.selectedPdpIndex = this.selectedPdpIndex + 1;
      }
      // if (this.poi.pointOfInterests.length - 1 < this.selectedPdpIndex) {
      //   this.getMorePDPs();
      // }
    },
    onChartSwipeRight() {
      if (this.selectedPdpIndex > 0) {
        this.selectedPdpIndex = this.selectedPdpIndex - 1;
      }
    },
    onLeakButton() {
      this.isNotLeak = false;
      this.isLeak = true;
      this.result = "leak";
      this.escalate = false;
    },
    addFoundLeak() {
      this.resetLeakErrors();
      const newLeak = new FoundLeakModel({
        foundBy: this.userId,
        dateFound: new Date()
      });
      this.leaks = [...this.leaks, newLeak];
    },
    addNoLeakFound() {
      this.resetLeakErrors();
      if(!this.noLeak) {
        const newLeak = new NoLeakModel({
          foundBy: this.userId,
          dateFound: new Date()
        });
        this.noLeak = newLeak;
      }
    },
    onLeakDelete(index) {
      this.resetLeakErrors();
      this.leaks.splice(index, 1);
    },
    onNoLeakDelete() {
      this.resetLeakErrors();
      this.noLeak = null;
      this.onLeakButton();
    },
    onNoLeakButton() {
      this.result = "noleak";
      this.isNotLeak = true;
      this.isLeak = false;
      this.escalate = false;
      this.addNoLeakFound();
    },
    onEscalateButton() {
      this.areYouSureEscalate = !this.areYouSureEscalate;
    },
    isNoLeakMissingData() {
      let missingData = false;
      if(this.noLeak) {
        if(this.noLeak.reason == null || this.noLeak.reason == undefined) {
          this.highlightMissingLeakType = true;
          this.missingRequiredLeakDetails = true;
          missingData = true;
        }

        if (this.noLeak.equipmentUsed.length == 0) {
          this.highlightMissingEquipmentUsed = true;
          this.missingRequiredLeakDetails = true;
          missingData = true;
        }
      }

      return missingData;
    },
    isLeakMissingData() {
      let missingData = false;
      const hasGPS = this.doAllFoundLeaksContainGPS();
      const hasClassification = this.doAllFoundLeaksContainAClassification();
      const hasAssetLeaktype = this.leaks.find(l => l.assetLeakType == null);
      const hasEquipmentUsed = this.leaks.find(
        l => l.equipmentUsed.length == 0
      );
      const hasLeaktype = this.leaks.find(l => l.leakType == null);
      const missingWorkOrder = this.leaks.find(l => l.workOrder == null || l.workOrder.trim() == "");
      if (!hasGPS) {
        this.highlightMissingGPS = true;
        this.missingGPSDialog = true;
        missingData = true;
      }
      if (!hasClassification) {
        this.highlightMissingClassification = true;
        this.missingRequiredLeakDetails = true;
        missingData = true;
      }
      if (hasAssetLeaktype) {
        this.highlightMissingAssetLeakType = true;
        this.missingRequiredLeakDetails = true;
        missingData = true;
      }
      if (hasEquipmentUsed) {
        this.highlightMissingEquipmentUsed = true;
        this.missingRequiredLeakDetails = true;
        missingData = true;
      }
      if (hasLeaktype) {
        this.highlightMissingLeakType = true;
        this.missingRequiredLeakDetails = true;
        missingData = true;
      }
      if (this.setting.mandatoryWorkOrder && missingWorkOrder) {
        this.highlightMissingWorkOrder = true;
        this.missingRequiredLeakDetails = true;
        missingData = true;
      }

      if (!missingData) {
        // everything ok
        this.resetLeakErrors();
      }
      return missingData;
    },
    resetLeakErrors() {
      this.highlightMissingGPS = false;
      this.highlightMissingClassification = false;
      this.highlightMissingAssetLeakType = false;
      this.highlightMissingEquipmentUsed = false;
      this.highlightMissingLeakType = false;
    },
    onCompleteButton() {
      if (this.isLeak ? !this.isLeakMissingData(): !this.isNoLeakMissingData()) {
        if(this.noLeak && this.noLeak.reason == 4) {
          this.areYouSureCompleteComment = !this.areYouSureCompleteComment;
        }
        else {
          this.areYouSureComplete = !this.areYouSureComplete;
        }
      }
    },
    async onEscalateAnswer(answer) {
      if (answer) {
        this.result = "escalate";
        this.escalate = true;
        this.newComment = answer.comment;
        await this.addComment();
        this.onCompleteAnswer(answer);
      }
    },
    async onCompleteCommentAnswer(answer) {
      if (answer) {
        this.newComment = answer.comment;
        await this.addComment();
        this.onCompleteAnswer(answer);
      }
    },
    async onCompleteAnswer(answer) {
      if (answer) {
        if (this.isLeak && this.isLeakMissingData()) {
          return;
        }
        if(this.isNotLeak && this.isNoLeakMissingData()) {
          return;
        }

        if(this.newComment != null && this.newComment.trim() != "") {
          await this.addComment();
        }
        this.updating = true;
        const techData = JSON.parse(JSON.stringify(this.technicianData));
        techData.onSiteTimes[
          techData.onSiteTimes.length - 1
        ].leaving = new Date();

        const onSiteTimes = [...techData.onSiteTimes];
        onSiteTimes[techData.onSiteTimes.length - 1].leaving = new Date();
        var leakNoLeak = this.isLeak ? `Leak(s) found ${this.leaks.length}` : "No leak found";
        const newLog = CreateLogEntryObj(
          this.correlationData.id,
          `marked as completed-leaving site (${leakNoLeak})`
        );
        const data = {
          leaks: this.leaks,
          noLeak: this.noLeak,
          result: this.result,
          isNotLeak: this.isNotLeak,
          isLeak: this.isLeak,
          completed: true,
          returnedToAnalyst: true,
          onSiteTimes: onSiteTimes,
          dateCompleted: new Date(),
          escalate: this.escalate,
          logEntry: newLog
        };
        await this.$store.dispatch("poi/UpdatePoi", data);
        this.updating = false;
        this.onReturnToGroups();
      }
    },
    async onSaveButton() {
      // prevent us saving with missing data
      if (this.isLeak ? this.isLeakMissingData() : this.isNoLeakMissingData()) {
        return;
      }
      console.log(this.newComment);
      
      if(this.newComment != null && this.newComment.trim() != "") {
        await this.addComment();
      }
      const newLog = CreateLogEntryObj(
          this.correlationData.id,
          "Saving Poi"
        );

      this.updating = true;
      const data = {
        leaks: this.leaks,
        noLeak: this.noLeak,
        result: this.result,
        isNotLeak: this.isNotLeak,
        isLeak: this.isLeak,
        returnedToAnalyst: this.escalate ? true : false,
        logEntry: newLog
      };

      var techData = await this.$store.dispatch("poi/UpdatePoi", data);

      this.leaks = techData.leaks;
      this.updating = false;
    },
    getPixelsPerMillisecond(elementWidth, min, max) {
      // Calculate the total number of milliseconds for the correlation.
      let totalMS = Math.abs(max - min);
      // Calculate pixels per millisecond by dividing element width by total ms.
      return elementWidth / totalMS;
    },
    getPixelOffset(minTimeDelay, peak, pixelsPerMillisecond) {
      // Calculate the total number of milliseconds difference between the start if the recording and the peak.
      let milliseconds = Math.abs(peak - minTimeDelay);
      // Get offset by times milliseconds by specified pixels.
      return pixelsPerMillisecond * milliseconds;
    },
    getPercentage(a, b) {
      return (a / b) * 100;
    },
    findPercentage(a, b) {
      return (a * b) / 100;
    },
    findMaxIndex(arr) {
      if (!arr || arr.length === 0) {
        return -1;
      }
      let max = arr[0];
      let maxIndex = 0;
      for (let i = 1; i < arr.length; i++) {
        if (arr[i] > max) {
          maxIndex = i;
          max = arr[i];
        }
      }

      return maxIndex;
    },
    updateChartWidth() {
      if (this.$refs && this.$refs.chartDiv) {
        this.chartWidth = this.$refs.chartDiv.chart.chartWidth;
      }
    },
    setCommentVisible(val) {
      this.showComments = val;
    },
    setUpdating(val) {
      this.updating = val;
    }
  }
};
</script>

<style scoped>
.header {
  background-color: #f8f8f8;
}
.v-btn {
  color: var(--v-text-base) !important;
}

.data-header {
  font-weight: bolder;
  font-size: 1.1rem;
  color: black;
}

.data-content {
  font-weight: bold;
  font-size: 1.1rem;
  color: black;
}

.result-form {
  border: 1px solid hsla(0, 0%, 100%, 0.7);
}

.paused {
  background-image: url("/img/icons/pause-word-pattern.svg") !important;
}
.add-comment {
  left: 34vw;
  position: relative;
  bottom: 12vh;
}
.comment {
  margin-bottom: -6vh;
}

.section {
  margin-top: 1rem;
  margin-bottom: 2rem;
}

.btn--line {
  margin-left: -2vw;
}

img.slim-gutter {
  padding: 0.15rem;
}

.container.fluid.fill-height.photo {
  background-color: #00000020;
  padding: 0.5rem 0.25rem 0rem 0.5rem;
}

.flex.grow.center {
  align-items: center;
  justify-content: center;
  display: flex;
}

.flex.grow.center.left {
  justify-content: flex-start;
}
.flex.grow.center.right {
  justify-content: flex-end;
}

.photo-card {
  width: 100%;
}
</style>
